import React, { useState } from "react";
import PropTypes from "prop-types";

import {
  Button,
  Drawer,
  Hidden,
  IconButton,
  List,
  ListItem,
  Typography,
} from "@material-ui/core";

import { makeStyles, useTheme } from "@material-ui/core/styles";

import MenuIcon from "@material-ui/icons/Menu";
import HomeIcon from "@material-ui/icons/Home";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import ChromeReaderModeOutlinedIcon from "@material-ui/icons/ChromeReaderModeOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  Redirect,
  useLocation,
} from "react-router-dom";

import ProfileImage from "../assets/images/MT.jpg";

import HomeScreen from "./homeScreen";
import AboutScreen from "./aboutScreen";
import ResumeScreen from "./resumeScreen";
import PortfolioScreen from "./portfolioScreen";
import BlogScreen from "./blogScreen";
import ContactScreen from "./contactScreen";

import ScrollToTop from "../components/ScrollToTop";

import {
  getTranslate,
  changeLanguage,
  lang,
} from "../localization";

import "./index.css";

const drawerWidth = 280;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    background: "#060d18",
    direction: theme.direction,
  },

  desktopSidebar: {
    position: "fixed",
    zIndex: 1200,
    top: 0,
    bottom: 0,
    width: drawerWidth,
    left: theme.direction === "rtl" ? "auto" : 0,
    right: theme.direction === "rtl" ? 0 : "auto",
    borderRight:
      theme.direction === "rtl" ? "none" : "1px solid rgba(148,163,184,.10)",
    borderLeft:
      theme.direction === "rtl" ? "1px solid rgba(148,163,184,.10)" : "none",
    background:
      "linear-gradient(180deg,#050b14 0%,#06101c 54%,#050a12 100%)",
  },

  sidebarInner: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 0,
  },

  drawerPaper: {
    width: "min(86vw, 310px)",
    maxWidth: 310,
    color: "#fff",
    overflow: "hidden",
    background:
      "linear-gradient(180deg,#050b14 0%,#06101c 54%,#050a12 100%)",
    borderRight:
      theme.direction === "rtl" ? "none" : "1px solid rgba(148,163,184,.12)",
    borderLeft:
      theme.direction === "rtl" ? "1px solid rgba(148,163,184,.12)" : "none",
  },

  profileArea: {
    padding: "26px 22px 22px",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    borderBottom: "1px solid rgba(148,163,184,.09)",
  },

  profileLink: {
    display: "block",
    color: "inherit",
  },

  profileFrame: {
    width: 102,
    height: 102,
    padding: 3,
    marginBottom: 18,
    marginLeft: theme.direction === "rtl" ? "auto" : 0,
    marginRight: theme.direction === "rtl" ? 0 : "auto",
    borderRadius: "50%",
    background: "linear-gradient(135deg,#168bff,#57b7ff 55%,#168bff)",
    boxShadow: "0 14px 40px rgba(22,139,255,.17)",
  },

  profileImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
    border: "4px solid #050b14",
  },

  profileName: {
    color: "#f8fafc",
    fontWeight: 800,
    fontSize: 18,
    lineHeight: 1.45,
    marginBottom: 4,
  },

  profileRole: {
    color: "#718096",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: theme.direction === "rtl" ? 0 : ".12em",
    textTransform: theme.direction === "rtl" ? "none" : "uppercase",
  },

  navigationArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    overflowY: "auto",
    padding: "18px 12px",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      width: 4,
    },
  },

  navList: {
    width: "100%",
    padding: 0,
  },

  navItem: {
    width: "100%",
    minHeight: 52,
    margin: "5px 0",
    padding: "0 15px",
    borderRadius: 13,
    color: "#8998ae",
    position: "relative",
    direction: theme.direction,
    justifyContent: "flex-start",
    border: "1px solid transparent",
    transition:
      "background-color .2s ease, color .2s ease, border-color .2s ease, transform .2s ease",

    "&:hover": {
      color: "#fff",
      backgroundColor: "rgba(255,255,255,.035)",
      transform: theme.direction === "rtl" ? "translateX(-2px)" : "translateX(2px)",
    },

    "&:hover $navIcon": {
      color: "#47a5ff",
    },
  },

  navItemActive: {
    color: "#fff",
    borderColor: "rgba(22,139,255,.26)",
    background:
      theme.direction === "rtl"
        ? "linear-gradient(270deg,rgba(22,139,255,.17),rgba(22,139,255,.035))"
        : "linear-gradient(90deg,rgba(22,139,255,.17),rgba(22,139,255,.035))",

    "&:before": {
      content: '""',
      position: "absolute",
      top: 10,
      bottom: 10,
      width: 3,
      borderRadius: 10,
      background: "#168bff",
      left: theme.direction === "rtl" ? "auto" : 0,
      right: theme.direction === "rtl" ? 0 : "auto",
      boxShadow: "0 0 18px rgba(22,139,255,.45)",
    },

    "& $navIcon": {
      color: "#2c9cff",
    },
  },

  navIcon: {
    width: 20,
    height: 20,
    flexShrink: 0,
    marginRight: theme.direction === "rtl" ? 0 : 15,
    marginLeft: theme.direction === "rtl" ? 15 : 0,
    transition: "color .2s ease",
  },

  navText: {
    minWidth: 0,
    color: "inherit",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.6,
    letterSpacing: theme.direction === "rtl" ? 0 : ".04em",
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },

  bottomArea: {
    flexShrink: 0,
    padding: "16px 18px 18px",
    borderTop: "1px solid rgba(148,163,184,.09)",
  },

  languageSwitcher: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    direction: "ltr",
    gap: 4,
    padding: 4,
    borderRadius: 12,
    border: "1px solid rgba(148,163,184,.13)",
    backgroundColor: "rgba(255,255,255,.025)",
  },

  languageButton: {
    minWidth: 0,
    height: 34,
    borderRadius: 8,
    padding: "4px 10px",
    color: "#748198",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: ".03em",

    "&:hover": {
      color: "#fff",
      backgroundColor: "rgba(22,139,255,.07)",
    },
  },

  languageActive: {
    color: "#fff",
    backgroundColor: "rgba(22,139,255,.18)",
    boxShadow: "0 5px 18px rgba(22,139,255,.10)",
  },

  mobileHeader: {
    position: "fixed",
    zIndex: 1250,
    top: 0,
    left: 0,
    right: 0,
    height: 64,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    direction: theme.direction,
    gap: 10,
    padding: "8px 12px",
    background: "rgba(5,10,18,.92)",
    borderBottom: "1px solid rgba(148,163,184,.10)",
    backdropFilter: "blur(18px)",
  },

  mobileMenuButton: {
    width: 42,
    height: 42,
    flexShrink: 0,
    color: "#fff",
    borderRadius: 11,
    border: "1px solid rgba(148,163,184,.16)",
    background: "rgba(255,255,255,.025)",
  },

  mobileBrand: {
    minWidth: 0,
    flex: 1,
    color: "#dfe8f4",
    fontSize: 12,
    fontWeight: 800,
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mobileLanguage: {
    flexShrink: 0,
    display: "flex",
    direction: "ltr",
    gap: 3,
    padding: 3,
    borderRadius: 10,
    border: "1px solid rgba(148,163,184,.14)",
    background: "rgba(255,255,255,.025)",
  },

  mobileLanguageButton: {
    minWidth: 42,
    height: 30,
    padding: "2px 6px",
    fontSize: 9,
    fontWeight: 800,
    color: "#758198",
    borderRadius: 7,
  },

  mobileLanguageActive: {
    color: "#fff",
    background: "rgba(22,139,255,.19)",
  },

  content: {
    width: "100%",
    minWidth: 0,
    minHeight: "100vh",
    position: "relative",
    overflowX: "hidden",
    background: "#070d18",

    [theme.breakpoints.up("md")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: theme.direction === "rtl" ? 0 : drawerWidth,
      marginRight: theme.direction === "rtl" ? drawerWidth : 0,
    },

    [theme.breakpoints.down("sm")]: {
      paddingTop: 64,
    },
  },
}));

const navItems = [
  { path: "/", exact: true, key: "home", icon: HomeIcon },
  { path: "/about", key: "about", icon: PersonOutlineIcon },
  { path: "/resume", key: "resume", icon: AssignmentOutlinedIcon },
  { path: "/portfolio", key: "portfolio", icon: WorkOutlineIcon },
  { path: "/blog", key: "blog", icon: ChromeReaderModeOutlinedIcon },
  { path: "/contact", key: "contact", icon: MailOutlineIcon },
];

function SiteShell(props) {
  const { window } = props;
  const classes = useStyles();
  const theme = useTheme();
  const translate = getTranslate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen((value) => !value);
  };

  const activePage = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const LanguageSwitcher = ({ mobile = false }) => (
    <div className={mobile ? classes.mobileLanguage : classes.languageSwitcher}>
      <Button
        className={`${mobile ? classes.mobileLanguageButton : classes.languageButton} ${
          lang === "en"
            ? mobile
              ? classes.mobileLanguageActive
              : classes.languageActive
            : ""
        }`}
        onClick={() => changeLanguage("en")}
      >
        EN
      </Button>

      <Button
        className={`${mobile ? classes.mobileLanguageButton : classes.languageButton} ${
          lang === "fa"
            ? mobile
              ? classes.mobileLanguageActive
              : classes.languageActive
            : ""
        }`}
        onClick={() => changeLanguage("fa")}
        style={{ fontFamily: "IRANSans", letterSpacing: 0 }}
      >
        فارسی
      </Button>
    </div>
  );

  const drawerContent = (
    <div className={classes.sidebarInner}>
      <div className={classes.profileArea}>
        <Link to="/" className={classes.profileLink} onClick={() => setMobileOpen(false)}>
          <div className={classes.profileFrame}>
            <img
              src={ProfileImage}
              alt={translate.name}
              className={classes.profileImage}
            />
          </div>

          <Typography className={classes.profileName}>{translate.name}</Typography>
          <Typography className={classes.profileRole}>{translate.navRole}</Typography>
        </Link>
      </div>

      <div className={classes.navigationArea}>
        <List className={classes.navList}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = activePage(item);

            return (
              <ListItem
                button
                key={item.path}
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`${classes.navItem} ${active ? classes.navItemActive : ""}`}
                aria-current={active ? "page" : undefined}
              >
                <Icon className={classes.navIcon} />
                <Typography className={classes.navText}>{translate[item.key]}</Typography>
              </ListItem>
            );
          })}
        </List>
      </div>

      <div className={classes.bottomArea}>
        <LanguageSwitcher />
      </div>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <div className={classes.root}>
      <Hidden smDown implementation="css">
        <aside className={classes.desktopSidebar}>{drawerContent}</aside>
      </Hidden>

      <Hidden mdUp implementation="css">
        <div className={classes.mobileHeader}>
          <IconButton
            className={classes.mobileMenuButton}
            onClick={handleDrawerToggle}
            aria-label={translate.openNavigation}
          >
            <MenuIcon />
          </IconButton>

          <Typography className={classes.mobileBrand}>{translate.name}</Typography>
          <LanguageSwitcher mobile />
        </div>

        <Drawer
          container={container}
          variant="temporary"
          anchor={theme.direction === "rtl" ? "right" : "left"}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{ keepMounted: true }}
        >
          {drawerContent}
        </Drawer>
      </Hidden>

      <main className={classes.content}>
        <ScrollToTop />

        <Switch>
          <Route exact path="/" component={HomeScreen} />
          <Route path="/about" component={AboutScreen} />
          <Route path="/resume" component={ResumeScreen} />
          <Route path="/portfolio" component={PortfolioScreen} />
          <Route path="/blog" component={BlogScreen} />
          <Route path="/contact" component={ContactScreen} />
          <Redirect to="/" />
        </Switch>
      </main>
    </div>
  );
}

function ResponsiveDrawer(props) {
  return (
    <Router>
      <SiteShell {...props} />
    </Router>
  );
}

ResponsiveDrawer.propTypes = {
  window: PropTypes.func,
};

export default ResponsiveDrawer;
